import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { VerifiedBadge } from '@/components/ui/VerifiedBadge';
import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import type { AgreementStatus, VerifyAgreement } from '@/lib/database.types';
import { formatDateTime } from '@/lib/format';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { verifyAgreement } from '@/lib/verify';

function docNumber(id: string): string {
  return `ΧΕΙΡ-${id.slice(0, 8).toUpperCase()}`;
}

type VerifyParty = {
  name: string | null;
  role: string;
  verified: boolean;
  agreed: boolean;
  agreed_at: string | null;
  withdrawn_at: string | null;
};
type VerifyAgreementWithParties = VerifyAgreement & { parties: VerifyParty[] | null };

export default function VerifyScreen() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { id } = useLocalSearchParams<{ id?: string }>();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const [data, setData] = useState<VerifyAgreementWithParties | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    if (!id) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    try {
      const res = await verifyAgreement(id);
      if (res) setData(res as VerifyAgreementWithParties);
      else setNotFound(true);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const parties: VerifyParty[] = data?.parties ?? [];
  const activeParties = parties.filter((p) => !p.withdrawn_at);
  const allActiveSigned = activeParties.length > 0 && activeParties.every((p) => p.agreed);

  return (
    <ScrollView
      style={styles.flex}
      contentContainerStyle={[
        styles.content,
        { paddingTop: insets.top + spacing.xl, paddingBottom: insets.bottom + spacing.xl },
      ]}
    >
      <View style={styles.header}>
        <Text style={styles.brand}>Χειραψία 🤝</Text>
        <Text style={styles.headerSub}>{t('verifyScreen.headerSub')}</Text>
      </View>

      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : notFound || !data ? (
        <View style={styles.card}>
          <Ionicons name="alert-circle" size={40} color={colors.danger} />
          <Text style={styles.invalidTitle}>{t('verifyScreen.notFoundTitle')}</Text>
          <Text style={styles.muted}>
            {t('verifyScreen.notFoundBody')}
          </Text>
        </View>
      ) : (
        <>
          <View style={[styles.card, styles.validCard]}>
            <Ionicons name="checkmark-circle" size={44} color={colors.success} />
            <Text style={styles.validTitle}>{t('verifyScreen.validTitle')}</Text>
            <Text style={styles.docNo}>{docNumber(data.id)}</Text>
          </View>

          <View style={styles.card}>
            <Row label={t('verifyScreen.field_title')} value={data.title} styles={styles} />
            <View style={styles.row}>
              <Text style={styles.rowLabel}>{t('verifyScreen.field_status')}</Text>
              <StatusBadge status={data.status as AgreementStatus} />
            </View>
            <Row
              label={t('verifyScreen.field_type')}
              value={
                data.type === 'one_off' ? t('verifyScreen.type_oneOff')
                  : data.type === 'fixed_term' ? t('verifyScreen.type_fixedTerm')
                  : t('verifyScreen.type_longTerm')
              }
              styles={styles}
            />
            <Row label={t('verifyScreen.field_created')} value={formatDateTime(data.created_at)} styles={styles} />
          </View>

          <View style={styles.card}>
            <Text style={styles.sectionTitle}>{t('verifyScreen.sectionTitle_parties')}</Text>
            {parties.map((p, i) => (
              <Party
                key={i}
                name={p.name}
                verified={p.verified}
                agreed={p.agreed}
                agreedAt={p.agreed_at}
                withdrawnAt={p.withdrawn_at}
                styles={styles}
                t={t}
              />
            ))}
            {allActiveSigned && (
              <Text style={styles.seal}>{t('verifyScreen.bothSignedSeal')}</Text>
            )}
          </View>

          <Text style={styles.note}>
            {t('verifyScreen.privacyNote')}
          </Text>
        </>
      )}

      <Pressable onPress={() => router.replace('/')} hitSlop={8} style={styles.homeBtn}>
        <Text style={styles.homeText}>{t('verifyScreen.action_openApp')}</Text>
      </Pressable>
    </ScrollView>
  );
}

function Row({ label, value, styles }: { label: string; value: string; styles: ReturnType<typeof makeStyles> }) {
  return (
    <View style={styles.row}>
      <Text style={styles.rowLabel}>{label}</Text>
      <Text style={styles.rowValue} numberOfLines={2}>
        {value}
      </Text>
    </View>
  );
}

function Party({
  name,
  verified,
  agreed,
  agreedAt,
  withdrawnAt,
  styles,
  t,
}: {
  name: string | null;
  verified: boolean;
  agreed: boolean;
  agreedAt: string | null;
  withdrawnAt: string | null;
  styles: ReturnType<typeof makeStyles>;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <View style={styles.party}>
      <View style={styles.partyHead}>
        <Text style={styles.partyName}>{name?.trim() || t('verifyScreen.defaultUser')}</Text>
        {verified && !withdrawnAt && <VerifiedBadge compact />}
      </View>
      {withdrawnAt ? (
        <Text style={[styles.partyMark, styles.withdrawn]}>
          {t('verifyScreen.withdrewOn', { date: formatDateTime(withdrawnAt) })}
        </Text>
      ) : (
        <Text style={[styles.partyMark, agreed ? styles.signed : styles.unsigned]}>
          {agreed
            ? `${t('verifyScreen.signedDigitally')}${agreedAt ? ` · ${formatDateTime(agreedAt)}` : ''}`
            : t('verifyScreen.pendingSignature')}
        </Text>
      )}
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    content: { paddingHorizontal: spacing.lg, gap: spacing.lg },
    header: { alignItems: 'center', gap: spacing.xs },
    brand: { color: colors.primary, fontSize: fontSize.xl, fontWeight: '800' },
    headerSub: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      textTransform: 'uppercase',
      letterSpacing: 2,
    },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.sm,
      ...shadow.card,
    },
    validCard: { alignItems: 'center', borderColor: colors.success },
    validTitle: { color: colors.success, fontSize: fontSize.lg, fontWeight: '800', textAlign: 'center' },
    docNo: { color: colors.textMuted, fontSize: fontSize.md, fontWeight: '600', letterSpacing: 1 },
    invalidTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700', textAlign: 'center' },
    muted: { color: colors.textMuted, fontSize: fontSize.sm, textAlign: 'center', lineHeight: 20 },
    sectionTitle: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    row: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: spacing.md,
      alignItems: 'flex-start',
    },
    rowLabel: { color: colors.textMuted, fontSize: fontSize.md },
    rowValue: { color: colors.text, fontSize: fontSize.md, fontWeight: '600', flexShrink: 1, textAlign: 'right' },
    party: {
      gap: 2,
      paddingTop: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    partyHead: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
    partyName: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    partyMark: { fontSize: fontSize.sm, fontWeight: '600' },
    signed: { color: colors.success },
    unsigned: { color: colors.textMuted },
    withdrawn: { color: colors.danger },
    seal: {
      color: colors.success,
      fontSize: fontSize.sm,
      fontWeight: '700',
      textAlign: 'center',
      borderWidth: 1.5,
      borderColor: colors.success,
      borderRadius: radius.md,
      paddingVertical: spacing.sm,
      marginTop: spacing.xs,
    },
    note: { color: colors.textMuted, fontSize: fontSize.sm, textAlign: 'center', lineHeight: 19 },
    homeBtn: { alignSelf: 'center', paddingVertical: spacing.sm },
    homeText: { color: colors.primaryLight, fontSize: fontSize.md, fontWeight: '700' },
  });
}
